// storage
import { storage, firestoreConstructors, firestore } from '../../plugins/firebase'
import { parseDevice, parseCampaignData } from '../helpers'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import _cloneDeep from 'lodash/cloneDeep'
import _merge from 'lodash/merge'
import { DEFAULT_PLUVIOMETRIC_CHART_DATA, DEFAULT_FIELD_SOURCE } from '../../constants/const'

/**
 * Normalizes chart document from Firestore (backend may store as { data: { ... } } and use basePress for chart pressure).
 * Ensures pressureRange.chartPressure and full nested structure so the UI never hits undefined.
 */
function normalizeChartDoc (raw) {
  if (!raw || typeof raw !== 'object') return null
  const data = raw.data != null ? raw.data : raw
  if (!data || typeof data !== 'object') return data
  const out = _cloneDeep(data)
  const basePress = data.advancedConfiguration && data.advancedConfiguration.pressure && data.advancedConfiguration.pressure.basePress
  if (basePress && (out.pressureRange == null || (out.pressureRange.chartPressure == null && out.pressureRange.chartPressure !== 0))) {
    out.pressureRange = { ...(out.pressureRange || {}), chartPressure: basePress.value, unit: basePress.unit || (out.pressureRange && out.pressureRange.unit) || 'bar' }
  }
  return out
}

/**
 * It builds a URL to download a file from the storage.
 * @param {string} path full path to access the file in the storage.
 * @returns {Promise<string>} download url or empty string if an error ocurred.
 */
export async function getFileDownloadURL (path) {
  let url = ''

  try {
    const fileRef = storage.ref(path)
    url = await fileRef.getDownloadURL()
  } catch (error) {
    console.error('Error while getting URL to download file:', JSON.stringify(error))
    throw error
  }

  return url
}

/**
 * It gets the parsed pluviometric charts data of a device.
 * Charts without a valid parsedChartId (e.g. webhook failed) get default data so the UI still opens.
 * @param {Array} charts the pluviometric chart metadata as stored on the device (with parsedChartId).
 * @returns {Promise<Array>} a promise that will resolve to the charts data (one entry per chart, same order).
 */
export async function getParsedPluviometricChartsData (charts) {
  if (!charts || !charts.length) return []

  const chartIds = charts.map(chart => (chart.parsedChartId && String(chart.parsedChartId).trim()) || '')
  const validIds = chartIds.filter(id => id.length > 0)

  // Build one slot per chart; missing or failed webhook charts get default data
  const data = charts.map(() => _cloneDeep(DEFAULT_PLUVIOMETRIC_CHART_DATA))

  if (validIds.length === 0) {
    return data
  }

  try {
    const db = await firestore
    const chartsQueries = []
    const idsToFetch = [...validIds]
    while (idsToFetch.length) {
      const chartIdsBatch = idsToFetch.splice(0, 10)
      const chartsQuery = db.collection('app/v1/pluviometricCharts').where(firestoreConstructors.FieldPath.documentId(), 'in', chartIdsBatch).get()
      chartsQueries.push(chartsQuery)
    }
    const chartsQueriesSnapshot = await Promise.all(chartsQueries)
    const fetchedDocs = chartsQueriesSnapshot.reduce((acc, snapshot) => {
      snapshot.forEach(doc => acc.push(doc))
      return acc
    }, [])

    const idToDoc = new Map(fetchedDocs.map(doc => [doc.id, doc]))
    chartIds.forEach((id, index) => {
      if (!id) return
      const doc = idToDoc.get(id)
      if (doc && doc.data()) {
        const raw = doc.data()
        const normalized = normalizeChartDoc(raw)
        if (normalized) {
          const deviceChartFieldSource = (charts[index] && charts[index].fieldSource) || {}
          data[index] = _merge({}, _cloneDeep(DEFAULT_PLUVIOMETRIC_CHART_DATA), normalized, {
            id: doc.id,
            fieldSource: { ...DEFAULT_FIELD_SOURCE, ...(normalized.fieldSource || raw.fieldSource || {}), ...deviceChartFieldSource }
          })
          if (!data[index].pressureRange || typeof data[index].pressureRange !== 'object') {
            data[index].pressureRange = { unit: 'bar' }
          }
        }
      }
    })
  } catch (error) {
    console.error('Error while getting parsed pluviometric charts:', JSON.stringify(error))
    throw error
  }

  return data
}

/**
 * It gets the pluviometric chart constants.
 * @returns {Promise<object>} a promise that will resolve to the pluviometric chart constants data.
 */
export async function getPluviometricChartConstants () {
  let docData = {}

  try {
    const db = await firestore
    const docRef = await db.collection('app/v1/constants').doc('pluviometricChart').get()
    docData = docRef.data()
  } catch (error) {
    console.error('Error while getting pluviometric chart constants:', JSON.stringify(error))
    throw error
  }

  return docData
}

/**
 * It gets all the organizations docs.
 * @returns {Promise<object>} a promise that will resolve to all of the organizations docs.
 */
export async function getOrganizations () {
  let organizations = []

  try {
    const db = await firestore
    const querySnapshot = await db.collection('app/v1/organizations').get()
    organizations = querySnapshot.docs.reduce((result, doc) => {
      const data = doc.data()
      if (!isEmpty(data)) {
        result.push({
          id: doc.id,
          name: data.name || data.alias,
          devices: data.devices
        })
      }

      return result
    }, [])

    organizations = sortBy(organizations, ['name'])
  } catch (error) {
    console.error('Error while getting the organizations', JSON.stringify(error))
    throw error
  }

  return organizations
}

/**
 * It gets the devices from a list of the devices reference.
 * @param {Array} orgDevicesList a list of the reference to the devices.
 * @param {object} db optional firestore db
 * @returns {Promise<object>} a promise that will resolve to the devices data.
 */
export async function getDevices (orgDevicesList, db = null) {
  db = db || await firestore
  const devicesData = []

  try {
    const devicesQueries = []
    const devicesIds = uniq(map(orgDevicesList, (device) => device.id))

    while (devicesIds.length) {
      const devicesIdsBatch = devicesIds.splice(0, 10)
      const devicesQuery = db.collection('app/v1/devices').where(firestoreConstructors.FieldPath.documentId(), 'in', devicesIdsBatch).get()
      devicesQueries.push(devicesQuery)
    }
    const devicesQueriesSnapshot = await Promise.all(devicesQueries)
    const devices = devicesQueriesSnapshot.reduce((devicesAcc, snapshot) => {
      snapshot.forEach(doc => { devicesAcc.push(doc) })
      return devicesAcc
    }, [])
    devices.forEach(device => {
      if (device.data()) {
        devicesData.push(parseDevice(device.id, device.data()))
      }
    })
  } catch (error) {
    console.error('Error while getting the devices', JSON.stringify(error))
    throw error
  }

  return devicesData
}

export async function getOrganizationDevices (organizationId, db = null) {
  db = db || await firestore
  let devicesData = []
  try {
    const devicesQuery = await db.collection('app/v1/devices').where('organization', '==', db.collection('app/v1/organizations').doc(organizationId)).get()
    if (devicesQuery.size) {
      devicesData = devicesQuery.docs.map(device => parseDevice(device.id, device.data()))
    }
  } catch (error) {
    console.error('Error while getting the devices', JSON.stringify(error))
    throw error
  }

  return devicesData
}

/**
 * Finds a device by ID
 * @param {string} deviceID the device ID
 * @param {object} db optional firestore db
 * @returns {Promise<object>} a promise that will resolve to the device data.
 */
export async function getDevice (deviceID, db = null) {
  let docData = {}

  try {
    db = db || await firestore
    const docRef = await db.collection('app/v1/devices').doc(deviceID).get()
    docData = docRef.data()
  } catch (error) {
    console.error('Error while getting device by ID', deviceID, JSON.stringify(error))
    throw error
  }

  return docData
}

/**
 * Gets the latest terms and conditions document ordered by version field
 * @param {object} db optional firestore db
 * @returns {Promise<object|null>} a promise that will resolve to the latest terms and conditions or null if not found
 */
export async function getTermsAndConditions (version) {
  let latestTermsAndConditions = null

  try {
    const db = await firestore
    const termsAndConditionsRef = db.collection('app/v1/constants/campaigns/termsAndConditions')

    let querySnapshot
    if (version) {
      querySnapshot = await termsAndConditionsRef
        .where('version', '==', version)
        .limit(1)
        .get()
    } else {
      // Query to get the latest document ordered by version field (descending)
      querySnapshot = await termsAndConditionsRef
        .orderBy('version', 'desc')
        .limit(1)
        .get()
    }


    if (!querySnapshot.empty) {
      const latestDoc = querySnapshot.docs[0]
      latestTermsAndConditions = {
        id: latestDoc.id,
        ...latestDoc.data()
      }
    } else {
      console.warn('No terms and conditions documents found')
    }
  } catch (error) {
    console.error('Error while getting latest terms and conditions:', JSON.stringify(error))
    throw error
  }

  return latestTermsAndConditions
}

export async function getCampaignById (campaignId) {
  try {
    const db = await firestore
    const campaignRef = db.collection('app/v1/campaigns').doc(campaignId)
    const campaignDoc = await campaignRef.get()
    return parseCampaignData(campaignDoc)
  } catch (error) {
    throw error
  }
}



// WEBPACK FOOTER //
// ./src/lib/crudOperations/read.js